import React, { useEffect, useState } from 'react'
import restaurantService from '../services/restaurantService'
import { Restaurant } from '../types/restaurant'
import RestaurantCard from './RestaurantCard'
import { useTranslation } from '../hooks/use-i18n'

interface RestaurantsSectionProps {
  refreshKey?: number
  onOpenCreate: () => void
}

const RestaurantsSection: React.FC<RestaurantsSectionProps> = ({ refreshKey = 0, onOpenCreate }) => {
  const { t } = useTranslation()
  const [restaurants, setRestaurants] = useState<Restaurant[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchRestaurants = async () => {
      try {
        setIsLoading(true)
        const userRestaurants = await restaurantService.getUserRestaurants()
        setRestaurants(userRestaurants || [])
      } catch {
        setRestaurants([])
      } finally {
        setIsLoading(false)
      }
    }

    fetchRestaurants()
  }, [refreshKey])

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500" />
      </div>
    )
  }

  return (
    <section className="py-4 sm:py-6 lg:py-8">
      <h2 className="text-lg sm:text-2xl lg:text-4xl font-bold text-white mb-6 sm:mb-8">
        {t('dashboard.restaurants.title')}
      </h2>

      {restaurants.length === 0 ? (
        <div className="text-center py-6 sm:py-8 lg:py-16">
          <div className="bg-[#1a1a1a] border border-white/10 rounded-xl sm:rounded-2xl lg:rounded-3xl p-4 sm:p-6 lg:p-12 max-w-xs sm:max-w-sm lg:max-w-md mx-auto">
            <div className="text-4xl sm:text-6xl lg:text-8xl mb-3 sm:mb-4 lg:mb-6">🍽️</div>
            <h3 className="text-lg sm:text-xl lg:text-2xl font-semibold mb-2 sm:mb-3 lg:mb-4 text-white">
              {t('dashboard.restaurants.empty.title')}
            </h3>
            <p className="text-white/80 mb-4 sm:mb-6 lg:mb-8 leading-relaxed text-xs sm:text-sm lg:text-base">
              {t('dashboard.restaurants.empty.body')}
            </p>
            <button
              type="button"
              onClick={onOpenCreate}
              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 sm:px-6 lg:px-8 py-2 sm:py-3 rounded-lg transition-all duration-300 font-medium text-xs sm:text-sm lg:text-base w-full"
            >
              {t('dashboard.createButton')}
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-8">
          {restaurants.map((restaurant, index) => (
            <div
              key={restaurant._id || restaurant.id}
              className="animate-fade-in"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <RestaurantCard restaurant={restaurant} />
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

export default RestaurantsSection
